import { canonicalInvoiceStatus } from './invoice-status';

import type { InvoiceRow } from '../types';

// Whether a note row is a Credit or Debit note, handling the typed invoice_type
// (Credit_Note/Debit_Note) and the legacy Adjustment + note_type sign. Null for non-notes.
export function resolveNoteKind(invoice: {
  invoice_type: string | null;
  note_type: string | null;
}): 'Credit' | 'Debit' | null {
  if (invoice.invoice_type === 'Credit_Note') return 'Credit';
  if (invoice.invoice_type === 'Debit_Note') return 'Debit';
  if (invoice.invoice_type !== 'Adjustment') return null;
  const nt = invoice.note_type?.toLowerCase() ?? '';
  if (nt === 'credit' || nt === 'credit_note') return 'Credit';
  if (nt === 'debit' || nt === 'debit_note') return 'Debit';
  return null;
}

// Purchase-side invoices are raised (received and recorded from the supplier),
// never issued — issuing is the sale-side freeze. The stored status value is
// shared between the directions, so the verb is corrected at display time only.
export function invoiceStatusLabel(invoice: {
  status: string | null;
  invoice_direction: string | null;
}): string | null {
  // Legacy 'Sent' rows are the same issued state under an old name, so they
  // read like Issued (Raised on the purchase side), not the raw legacy word.
  const status = canonicalInvoiceStatus(invoice.status);
  if (status !== 'Issued') return status;
  const isPurchase = (invoice.invoice_direction ?? '').toLowerCase() === 'purchase';
  return isPurchase ? 'Raised' : status;
}

export function invoiceTypeLabel(invoice: InvoiceRow): string {
  const noteKind = resolveNoteKind(invoice);
  if (noteKind !== null) return noteKind === 'Debit' ? 'Debit Note' : 'Credit Note';
  if (invoice.invoice_type === 'Advance') return 'Advance invoice';
  if (invoice.invoice_type === 'Provisional') return 'Provisional invoice';
  if (invoice.invoice_type === 'Final_Settlement' || invoice.invoice_type === 'Final Settlement') {
    return 'Final Settlement invoice';
  }
  return invoice.invoice_type ?? 'invoice';
}
